import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Clock, Video, FileText, MessageSquare, Plus, X, Mail, Copy, Check, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

/**
 * BriteLabCalendarTab — diary view + meeting scheduler for a BriteLab.
 *
 * Pulls the aggregated /api/britelabs/:id/calendar feed (meetings,
 * mini-project deadlines, recent posts) and renders a date-grouped
 * list. Lab members can also schedule a Teams call: the dialog
 * accepts a paste-in join URL, captures attendees from the roster,
 * and the backend mails an .ics invite to each picked attendee.
 *
 * Props:
 *   labId          – string, required
 *   currentUserId  – string, used for "Yourself" pre-tick in dialog
 *   isMember       – bool, gates the "Schedule meeting" button
 */
export default function BriteLabCalendarTab({ labId, currentUserId, isMember }) {
  const [activities, setActivities] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [copied, setCopied] = useState(null);
  const token = sessionStorage.getItem('session_token');
  const headers = token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' };

  const loadCalendar = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/britelabs/${labId}/calendar`, { credentials: 'include', headers }).then(r => r.ok ? r.json() : { activities: [] }),
      fetch(`${BACKEND_URL}/api/britelabs/${labId}/members`,  { credentials: 'include', headers }).then(r => r.ok ? r.json() : { members: [] }),
    ]).then(([cal, mems]) => {
      setActivities(cal.activities || []);
      setMembers(mems.members || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadCalendar(); }, [labId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelMeeting = async (meeting_id) => {
    if (!window.confirm('Cancel this meeting? Attendees will not receive a notification.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/meetings/${meeting_id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      if (!res.ok) throw new Error();
      toast.success('Meeting cancelled');
      loadCalendar();
    } catch { toast.error('Could not cancel'); }
  };

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); setCopied(email); setTimeout(() => setCopied(null), 1500); }
    catch { toast.error('Could not copy'); }
  };

  // Group activities by date label (YYYY-MM-DD → "Friday, 27 February 2026")
  const groups = {};
  for (const a of activities) {
    if (!a.at) continue;
    const date = a.at.substring(0, 10);
    groups[date] = groups[date] || [];
    groups[date].push(a);
  }
  const sortedDates = Object.keys(groups).sort();
  const todayKey = new Date().toISOString().substring(0, 10);

  const formatDateLabel = (key) => {
    try {
      const d = new Date(key + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return key; }
  };
  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const iconFor = (kind) => {
    if (kind === 'meeting')  return <Video    className="h-4 w-4 text-brand-navy" />;
    if (kind === 'deadline') return <Clock    className="h-4 w-4 text-amber-600" />;
    return <MessageSquare className="h-4 w-4 text-slate-500" />;
  };

  return (
    <div className="space-y-6" data-testid="britelab-calendar-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold font-heading text-slate-800 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-brand-navy" /> BriteLab Diary
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Meetings, deadlines, and recent activity in one place.</p>
        </div>
        {isMember && (
          <Button onClick={() => setShowSchedule(true)} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="schedule-meeting-btn">
            <Plus className="h-4 w-4 mr-2" /> Schedule meeting
          </Button>
        )}
      </div>

      {/* Members panel — shows email addresses to co-members */}
      {members.length > 0 && (
        <Card className="p-4" data-testid="lab-members-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-brand-navy" />
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Lab members</h4>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center justify-between gap-2 p-2 rounded border border-slate-100 bg-slate-50/40" data-testid={`member-row-${m.user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{m.name}</p>
                  {(m.role || m.organisation) && (
                    <p className="text-xs text-slate-500 truncate">{[m.role, m.organisation].filter(Boolean).join(' · ')}</p>
                  )}
                  {m.email && (
                    <a href={`mailto:${m.email}`} className="text-xs text-brand-navy hover:underline inline-flex items-center gap-1 mt-0.5" data-testid={`member-email-${m.user_id}`}>
                      <Mail className="h-3 w-3" /> {m.email}
                    </a>
                  )}
                </div>
                {m.email && (
                  <button onClick={() => copyEmail(m.email)} title="Copy email"
                    className="p-1.5 rounded hover:bg-white text-slate-400 hover:text-brand-navy transition-colors"
                    data-testid={`copy-email-${m.user_id}`}>
                    {copied === m.email ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-slate-400 italic">Loading diary…</p>
      ) : sortedDates.length === 0 ? (
        <Card className="p-8 text-center">
          <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No activities yet. Schedule a meeting or add mini-project deadlines.</p>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="calendar-feed">
          {sortedDates.map(dateKey => (
            <div key={dateKey} data-testid={`day-${dateKey}`}>
              <div className="sticky top-20 z-10 bg-slate-50/95 backdrop-blur py-1.5 -mx-1 px-1 mb-2">
                <p className={`text-xs font-bold uppercase tracking-wide ${dateKey === todayKey ? 'text-brand-sage' : 'text-slate-500'}`}>
                  {formatDateLabel(dateKey)} {dateKey === todayKey && <span className="ml-1 px-1.5 py-0.5 rounded bg-brand-sage/15">Today</span>}
                </p>
              </div>
              <div className="space-y-2">
                {groups[dateKey].map((a, i) => (
                  <Card key={`${dateKey}-${i}`} className="p-3 flex items-start gap-3 hover:shadow-sm transition-shadow" data-testid={`activity-${a.kind}-${i}`}>
                    <div className="flex-shrink-0 mt-0.5">{iconFor(a.kind)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[10px] font-mono text-slate-400">{formatTime(a.at)}</span>
                        <span className="text-[10px] uppercase font-bold tracking-wide text-slate-400">{a.kind}</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{a.title}</p>
                      {a.description && <p className="text-xs text-slate-500 mt-1 whitespace-pre-line line-clamp-2">{a.description}</p>}
                      {a.kind === 'meeting' && a.teams_url && (
                        <a href={a.teams_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-xs text-brand-navy hover:underline">
                          <Video className="h-3 w-3" /> Join call
                        </a>
                      )}
                      {a.kind === 'meeting' && a.organizer && (
                        <p className="text-[11px] text-slate-400 mt-1">Organised by {a.organizer}</p>
                      )}
                      {a.kind === 'post' && a.author && (
                        <p className="text-[11px] text-slate-400 mt-1">by {a.author}</p>
                      )}
                    </div>
                    {a.kind === 'meeting' && (a.organizer || currentUserId) && (
                      <button onClick={() => cancelMeeting(a.meeting_id)}
                        title="Cancel meeting"
                        className="p-1.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        data-testid={`cancel-meeting-${a.meeting_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ScheduleMeetingDialog
        open={showSchedule}
        onOpenChange={setShowSchedule}
        labId={labId}
        members={members}
        currentUserId={currentUserId}
        onCreated={() => { setShowSchedule(false); loadCalendar(); }}
      />
    </div>
  );
}


function ScheduleMeetingDialog({ open, onOpenChange, labId, members, currentUserId, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [teamsUrl, setTeamsUrl] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const token = sessionStorage.getItem('session_token');
  const headers = token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' };

  // Default: pre-tick all members (including self) when the dialog opens
  useEffect(() => {
    if (open) setAttendeeIds(members.map(m => m.user_id));
  }, [open, members]);

  const reset = () => {
    setTitle(''); setDescription(''); setTeamsUrl('');
    setDate(''); setStartTime(''); setEndTime(''); setAttendeeIds([]);
  };

  const toggle = (uid) => setAttendeeIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);

  const submit = async () => {
    if (!title || !date || !startTime || !endTime) {
      toast.error('Title, date, and times are required');
      return;
    }
    if (attendeeIds.length === 0) {
      toast.error('Pick at least one attendee');
      return;
    }
    // Build ISO strings (treated as local; backend stores as-is + ics
    // converts to UTC for export)
    const startIso = new Date(`${date}T${startTime}:00`).toISOString();
    const endIso = new Date(`${date}T${endTime}:00`).toISOString();
    if (endIso <= startIso) {
      toast.error('End time must be after start time');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/meetings`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({
          title, description, teams_url: teamsUrl,
          start_at: startIso, end_at: endIso, attendee_ids: attendeeIds,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Meeting scheduled — ${data.invites_sent} invite${data.invites_sent !== 1 ? 's' : ''} sent`);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e.message || 'Could not schedule');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Video className="h-5 w-5 text-brand-navy" /> Schedule a meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label htmlFor="mtg-title">Title *</Label>
            <Input id="mtg-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Kick-off call" data-testid="meeting-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="mtg-date">Date *</Label>
              <Input id="mtg-date" type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="meeting-date" />
            </div>
            <div>
              <Label htmlFor="mtg-start">Start *</Label>
              <Input id="mtg-start" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="meeting-start" />
            </div>
            <div>
              <Label htmlFor="mtg-end">End *</Label>
              <Input id="mtg-end" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} data-testid="meeting-end" />
            </div>
          </div>
          <div>
            <Label htmlFor="mtg-url">Teams / Zoom / Meet join URL</Label>
            <Input id="mtg-url" value={teamsUrl} onChange={e => setTeamsUrl(e.target.value)}
              placeholder="https://teams.microsoft.com/l/meetup-join/..." data-testid="meeting-url" />
            <p className="text-[11px] text-slate-400 mt-1">Create the call in your Teams/Zoom client, then paste the join URL here. We&apos;ll add it to the calendar invite.</p>
          </div>
          <div>
            <Label htmlFor="mtg-desc">Description</Label>
            <Textarea id="mtg-desc" value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="What's on the agenda?" data-testid="meeting-desc" />
          </div>
          <div>
            <Label>Attendees ({attendeeIds.length} of {members.length})</Label>
            <div className="mt-2 max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {members.map(m => {
                const checked = attendeeIds.includes(m.user_id);
                return (
                  <label key={m.user_id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${checked ? 'bg-brand-navy/5' : ''}`}
                    data-testid={`attendee-row-${m.user_id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(m.user_id)}
                      className="rounded text-brand-navy focus:ring-brand-navy" data-testid={`attendee-check-${m.user_id}`} />
                    <span className="flex-1 truncate text-slate-700">
                      {m.name} {m.user_id === currentUserId && <span className="text-[10px] text-slate-400 ml-1">(you)</span>}
                    </span>
                    {m.email && <span className="text-[11px] text-slate-400 truncate">{m.email}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="meeting-cancel-btn"><X className="h-4 w-4 mr-1" /> Cancel</Button>
          <Button disabled={submitting} onClick={submit} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="meeting-submit-btn">
            <FileText className="h-4 w-4 mr-2" /> {submitting ? 'Sending invites…' : 'Send invites'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
